/**
 * Tables pasted from a spreadsheet, for the research-design tools.
 *
 * Survey responses, expert ratings and answer sheets all arrive the same way: someone
 * selects a block in Excel, Google Sheets or SPSS and pastes it. That gives tab-separated
 * rows, usually with a header row of item names and often with a first column of IDs.
 * People also type data by hand with commas or spaces, and European spreadsheets export
 * CSV with semicolons because the comma is their decimal mark. This module reads all of
 * those, and reports how it read them so the widget can show the table back.
 *
 * The header row and the ID column are *guessed*, never silently assumed: the guess is
 * returned as `detected`, and a widget lets the reader overrule it. A numeric ID column
 * with no header ("1, 4, 5, 3") is indistinguishable from an item, which is exactly why
 * the override exists.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type Delimiter = "tab" | "semicolon" | "comma" | "space";

const LINE_BREAK = /\r\n|\r|\n/;
const WHITESPACE_RUN = /\s+/;

/**
 * Header names that mark the first column as row labels rather than data — so a numeric
 * ID column is still recognised when its header says what it is.
 */
const ID_HEADER =
  /^(?:id|#|no\.?|nr\.?|number|code|respondent|participant|student|pupil|examinee|candidate|subject|case|person|name|expert|rater|judge|panelist|panellist|reviewer)\b/i;

/** Cells that mean "no answer". Compared lower-cased and trimmed. */
const MISSING_TOKENS: ReadonlySet<string> = new Set([
  "",
  "na",
  "n/a",
  "#n/a",
  "nan",
  "null",
  "none",
  "missing",
  ".",
  "-",
  "–",
  "—",
  "?",
  "*",
]);

export function isMissingToken(cell: string): boolean {
  return MISSING_TOKENS.has(cell.trim().toLowerCase());
}

/** A cell a numeric matrix can hold: a number, or a recognised blank. */
export function isNumericCell(cell: string): boolean {
  return isMissingToken(cell) || parseNumberInput(cell) !== null;
}

/**
 * Tab if any line has one (a spreadsheet paste), then semicolon (European CSV, where the
 * comma is a decimal mark), then comma, and otherwise runs of spaces.
 */
export function detectDelimiter(lines: readonly string[]): Delimiter {
  if (lines.some((line) => line.includes("\t"))) {
    return "tab";
  }
  if (lines.some((line) => line.includes(";"))) {
    return "semicolon";
  }
  if (lines.some((line) => line.includes(","))) {
    return "comma";
  }
  return "space";
}

const SEPARATOR: Record<Exclude<Delimiter, "space">, string> = {
  tab: "\t",
  semicolon: ";",
  comma: ",",
};

/**
 * One line's cells. Delimited lines honour double-quoted fields (`"Smith, J"`), with a
 * doubled quote standing for a literal one, as CSV does; empty cells between two
 * delimiters are kept, because in a spreadsheet paste they are missing answers.
 */
export function splitLine(line: string, delimiter: Delimiter): string[] {
  if (delimiter === "space") {
    const trimmed = line.trim();
    return trimmed === "" ? [] : trimmed.split(WHITESPACE_RUN);
  }
  const separator = SEPARATOR[delimiter];
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (char === separator) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim());
  return cells;
}

/** Every non-blank line split into cells, padded so the table is rectangular. */
export function readRows(text: string): string[][] {
  const lines = text.split(LINE_BREAK).filter((line) => line.trim() !== "");
  const delimiter = detectDelimiter(lines);
  const rows = lines.map((line) => splitLine(line, delimiter));
  const width = Math.max(0, ...rows.map((row) => row.length));
  return rows.map((row) =>
    row.length < width
      ? [...row, ...new Array<string>(width - row.length).fill("")]
      : row
  );
}

export interface TableLayout {
  /** The first row names the columns. */
  header: boolean;
  /** The first column labels the rows (respondent IDs, expert names). */
  ids: boolean;
}

/**
 * Guess the layout.
 *
 * A header is a first row with a non-value somewhere after its first cell ("ID, Q1, Q2"
 * or "Q1, Q2, Q3"); a first row whose only non-value is its first cell is a data row
 * with an ID ("R01, 4, 5"). An ID column is one the header names ("ID", "Student",
 * "Expert") or one whose cells are mostly not values.
 */
export function detectLayout(
  rows: readonly (readonly string[])[],
  isValue: (cell: string) => boolean
): TableLayout {
  if (rows.length === 0) {
    return { header: false, ids: false };
  }
  const first = rows[0];
  const width = first.length;
  const header =
    width === 1
      ? rows.length > 1 && !isValue(first[0]) && isValue(rows[1][0])
      : first.slice(1).some((cell) => cell !== "" && !isValue(cell));
  const body = header ? rows.slice(1) : rows;
  const named = header && ID_HEADER.test(first[0].trim());
  const labelled =
    width > 1 &&
    body.length > 0 &&
    body.filter((row) => !isValue(row[0])).length * 2 >= body.length;
  return { header, ids: named || labelled };
}

export interface ParsedTable {
  /** Column names from the header row (ID column excluded), or null without one. */
  columns: string[] | null;
  /** Row labels from the ID column, or null without one. */
  ids: string[] | null;
  /** The header cell above the ID column ("Student"), when both exist. */
  idName: string | null;
  /** The data cells: one array per data row, ID column removed. */
  body: string[][];
  /** The layout applied — the guess, unless the reader overruled it. */
  layout: TableLayout;
  /** What detection guessed, so a checkbox can show it before it is touched. */
  detected: TableLayout;
}

export function parseTable(
  text: string,
  isValue: (cell: string) => boolean,
  overrides: Partial<TableLayout> = {}
): ParsedTable {
  const rows = readRows(text);
  const detected = detectLayout(rows, isValue);
  const layout: TableLayout = {
    header: overrides.header ?? detected.header,
    ids: overrides.ids ?? detected.ids,
  };
  const headerRow = layout.header && rows.length > 0 ? rows[0] : null;
  const dataRows = layout.header ? rows.slice(1) : rows;
  const skip = layout.ids ? 1 : 0;
  return {
    columns: headerRow ? headerRow.slice(skip) : null,
    ids: layout.ids ? dataRows.map((row) => row[0] ?? "") : null,
    idName: headerRow && layout.ids ? (headerRow[0] ?? "") || null : null,
    body: dataRows.map((row) => row.slice(skip)),
    layout,
    detected,
  };
}

/** Rows become columns — for a ratings table pasted the other way round. */
export function transposeTable(table: ParsedTable): ParsedTable {
  const width = Math.max(0, ...table.body.map((row) => row.length));
  const body = Array.from({ length: width }, (_, column) =>
    table.body.map((row) => row[column] ?? "")
  );
  return {
    ...table,
    columns: table.ids,
    ids: table.columns,
    idName: null,
    body,
  };
}

/**
 * Display names for a set of columns (or rows): the header text where there is one,
 * the fallback ("Item 3") where there is not, and a numbered suffix on a repeat, so
 * two columns both headed "Q1" stay distinguishable — and stay valid React keys.
 */
export function labelsFor(
  names: readonly string[] | null,
  count: number,
  fallback: (position: number) => string
): string[] {
  const seen = new Map<string, number>();
  return Array.from({ length: count }, (_, index) => {
    const base = names?.[index]?.trim() || fallback(index + 1);
    const times = (seen.get(base) ?? 0) + 1;
    seen.set(base, times);
    return times === 1 ? base : `${base} (${times})`;
  });
}

/* ------------------------------------------------------------------ export */

const CSV_NEEDS_QUOTES = /[",\r\n]/;

function csvCell(cell: string | number | null): string {
  if (cell === null) {
    return "";
  }
  const text = typeof cell === "number" ? csvNumber(cell) : cell;
  return CSV_NEEDS_QUOTES.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/**
 * A number as a CSV cell: machine notation (a period, no grouping) so any spreadsheet
 * reads it, rounded so `0.1 + 0.2` does not arrive as 0.30000000000000004.
 */
export function csvNumber(value: number, digits = 6): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  return String(Number(value.toFixed(digits)));
}

/** RFC 4180: comma-separated, quoted only where needed, CRLF line ends. */
export function toCsv(
  rows: readonly (readonly (string | number | null)[])[]
): string {
  return `${rows.map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

/** Tab-separated, which pastes straight back into a spreadsheet's cells. */
export function toTsv(
  rows: readonly (readonly (string | number | null)[])[]
): string {
  return rows
    .map((row) =>
      row
        .map((cell) =>
          typeof cell === "number" ? csvNumber(cell) : (cell ?? "")
        )
        .join("\t")
    )
    .join("\n");
}
